import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuth } from '@/contexts/AuthContext'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldSeparator,
} from '@/components/ui/field'
import {
  Mail,
  AlertCircle
} from 'lucide-react'
import tkLogo from '@/assets/tk-logga.png'
import { externalUrls } from '@/lib/constants'

export function LoginPage() {
  const navigate = useNavigate()
  const { signIn, signUp, signInWithGoogle, resetPassword, currentUser } = useAuth()

  const [isLogin, setIsLogin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard')
    }
  }, [currentUser, navigate])

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
    if (error) setError('') // Clear error when user starts typing
  }

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('E-post och lösenord är obligatoriska')
      return false
    }

    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError('Lösenorden matchar inte')
        return false
      }

      if (formData.password.length < 6) {
        setError('Lösenordet måste vara minst 6 tecken')
        return false
      }
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setLoading(true)
    setError('')

    try {
      let result

      if (isLogin) {
        result = await signIn(formData.email, formData.password)
      } else {
        result = await signUp(formData.email, formData.password)
      }

      if (result.error) {
        setError(result.error)
      } else {
        navigate('/dashboard')
      }
    } catch {
      setError('Ett oväntat fel uppstod. Försök igen.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError('')

    try {
      const result = await signInWithGoogle()
      if (result.error) {
        setError(result.error)
      } else {
        navigate('/dashboard')
      }
    } catch {
      setError('Google-inloggning misslyckades. Försök igen.')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Ange en e-postadress')
      return
    }

    setResetLoading(true)
    setError('')

    try {
      const result = await resetPassword(resetEmail)
      if (result.error) {
        setError(result.error)
      } else {
        setResetSuccess(true)
      }
    } catch {
      setError('Fel vid återställning av lösenord. Försök igen.')
    } finally {
      setResetLoading(false)
    }
  }

  const closeResetDialog = () => {
    setShowResetDialog(false)
    setResetEmail('')
    setResetSuccess(false)
    setError('')
  }

  const toggleMode = () => {
    setIsLogin(!isLogin)
    setError('')
    setFormData({
      email: formData.email,
      password: '',
      confirmPassword: ''
    })
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Left Column - Login Form */}
      <div className="flex flex-col gap-4 p-6 md:p-10">
        {/* Logo */}
        <div className="flex justify-center gap-2 md:justify-start">
          <a href={externalUrls.landingPage} className="flex items-center gap-2 font-medium">
            <img src={tkLogo} alt="Taxi Administration Logo" className="size-6" />
            <span className="font-semibold">taxiadministration.se</span>
          </a>
        </div>

        {/* Centered Form */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <FieldGroup>
                <div className="flex flex-col items-center gap-1 text-center">
                  <h1 className="text-2xl font-bold">
                    {isLogin ? 'Logga in på ditt konto' : 'Skapa ett konto'}
                  </h1>
                  <p className="text-muted-foreground text-sm text-balance">
                    {isLogin
                      ? 'Ange din e-post nedan för att logga in'
                      : 'Ange din e-post nedan för att skapa ett konto'
                    }
                  </p>
                </div>

                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <div className="text-red-800 font-medium">{error}</div>
                  </Alert>
                )}

                <Field>
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Fortsätt med Google
                  </Button>
                </Field>

                <FieldSeparator>eller använd e-post</FieldSeparator>

                <Field>
                  <Label htmlFor="email">E-postadress</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="din@email.se"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Field>

                <Field>
                  <div className="flex items-center">
                    <Label htmlFor="password">Lösenord</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => setShowResetDialog(true)}
                        className="ml-auto text-sm underline-offset-4 hover:underline"
                      >
                        Glömt lösenord?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder={isLogin ? "Ditt lösenord" : "Minst 6 tecken"}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </Field>

                {!isLogin && (
                  <Field>
                    <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="Upprepa lösenordet"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </Field>
                )}

                <Field>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? 'Laddar...' : (isLogin ? 'Logga in' : 'Skapa konto')}
                  </Button>

                  <FieldDescription className="text-center">
                    {isLogin ? 'Har du inget konto?' : 'Har du redan ett konto?'}{" "}
                    <button
                      type="button"
                      onClick={toggleMode}
                      className="underline underline-offset-4 hover:text-primary"
                    >
                      {isLogin ? 'Registrera här' : 'Logga in här'}
                    </button>
                  </FieldDescription>
                </Field>

                <p className="text-center text-sm text-muted-foreground">
                  {isLogin ? 'Genom att logga in' : 'Genom att skapa ett konto'} accepterar du våra{' '}
                  <Link to="/terms" className="underline underline-offset-4 hover:text-primary">
                    användarvillkor
                  </Link>
                  {' '}och{' '}
                  <Link to="/privacy" className="underline underline-offset-4 hover:text-primary">
                    integritetspolicy
                  </Link>
                  .
                </p>
              </FieldGroup>
            </form>
          </div>
        </div>
      </div>

      {/* Right Column - Cover Image/Gradient */}
      <div className="bg-muted relative hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        <img src={tkLogo} alt="Taxi Administration Logo" className="absolute top-6 right-6 size-24" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-white">
          <div className="max-w-md text-center">
            <h2 className="text-3xl font-bold mb-4">Välkommen till taxiadministration.se</h2>
            <p className="text-lg text-white/80">
              Snabb, enkel och korrekt löneberäkning för taxiåkare.
            </p>
          </div>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={closeResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Återställ lösenord</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {resetSuccess ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">E-post skickad!</h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Vi har skickat en återställningslänk till <strong>{resetEmail}</strong>.
                    Kontrollera din inkorg och följ instruktionerna för att återställa ditt lösenord.
                  </p>
                </div>
                <Button onClick={closeResetDialog} className="w-full">
                  Stäng
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Ange din e-postadress så skickar vi en länk för att återställa ditt lösenord.
                </p>

                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <div className="text-red-800 font-medium">{error}</div>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="resetEmail">E-postadress</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="resetEmail"
                      type="email"
                      placeholder="din@email.se"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10"
                      disabled={resetLoading}
                    />
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeResetDialog}
                    className="flex-1"
                    disabled={resetLoading}
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePasswordReset}
                    className="flex-1"
                    disabled={resetLoading || !resetEmail}
                  >
                    {resetLoading ? 'Skickar...' : 'Skicka återställningslänk'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
